import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { TimeFilterType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import {
  NoShowRateChart as AppointmentNoShowRateChart,
  AppointmentBreakdownChart,
} from '@frontend/analytics-appointment';
import {
  MessagingCategoriesOverviewCard,
  useIsMessagingAnalyticsDemoAccount,
  useMessagingAnalyticsDemoData,
  useMessagingAnalyticsShallowStore,
} from '@frontend/analytics-messaging';
import {
  PhoneIncomingCategoriesOverviewCard,
  useIsPhoneAnalyticsDemoAccount,
  usePhoneAnalyticsDemoData,
  usePhoneAnalyticsShallowStore,
} from '@frontend/analytics-phone';
import { GenerateAnalyticsReportCTA } from '@frontend/analytics-reporting';
import {
  useTimePeriodPopoverSelector,
  useAnalyticsOrgLocations,
  useHasAdminAccess,
  GeneralFeedbackButton,
  useHasAutoRecallAnalyticsFeature,
} from '@frontend/analytics-shared';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { AutoRecallOnDashboard } from '../components/homepage-analytics/auto-recall-on-dashboard';
import { PracticeAnalyticsOnDashboard } from '../components/homepage-analytics/practice-analytics-on-dashboard';
import { useIsPAEligibleAccount } from '../components/practice/hooks/use-is-pa-eligible-account';
import { RoiOverviewCards } from '../components/roi/roi-overview-cards/roi-overview-cards';
import { featureFlags } from '../feature-flags';

export const AnalyticsDashboard = () => {
  const { t } = useTranslation('analytics');
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const [hasRoiCards, setHasRoiCards] = useState<boolean | undefined>();

  const { startDate, endDate } = useTimePeriodPopoverSelector({
    defaultPeriod: 'last30Days',
  });

  const hasAutoRecallAnalyticsFeature = useHasAutoRecallAnalyticsFeature();
  const { isPAEligibleAccount } = useIsPAEligibleAccount();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const { preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount: getFeatureFlagValue(featureFlags.noShowRateRoiDemoData),
    module: 'ROI',
  });

  const messagingEnabled = useMessagingFeaturesEnabled();

  const isPhoneAnalyticsDemoAccount = useIsPhoneAnalyticsDemoAccount();
  const phoneAnalyticsDemoData = usePhoneAnalyticsDemoData();
  const { setStore: setPhoneAnalyticsStore } = usePhoneAnalyticsShallowStore('setStore');

  const isMessagingAnalyticsDemoAccount = useIsMessagingAnalyticsDemoAccount();
  const messagingAnalyticsDemoData = useMessagingAnalyticsDemoData();
  const { setStore: setMessagingAnalyticsStore } = useMessagingAnalyticsShallowStore('setStore');

  const hasAnalyticsReportingAccess =
    useHasAdminAccess() || hasSchemaACL(selectedLocationIds[0], Permission.ANALYTICS_REPORTING_ACCESS);

  const [filters, setFilters] = useState<AnalyticsCommonTypes.AnalyticsSummaryFilters>({
    endDate: dayjs(endDate),
    startDate: dayjs(startDate),
    timeZone: accessibleLocationData[selectedLocationIds[0]]?.timezone,
  });

  const handleUpdateFilters = (filters: Partial<AnalyticsCommonTypes.AnalyticsSummaryFilters>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }));
  };

  useEffect(() => {
    handleUpdateFilters({
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
    });
  }, [startDate, endDate]);

  useEffect(() => {
    setPhoneAnalyticsStore({ demoData: phoneAnalyticsDemoData });
  }, [phoneAnalyticsDemoData, setPhoneAnalyticsStore]);

  useEffect(() => {
    setPhoneAnalyticsStore({ isDemoAccount: isPhoneAnalyticsDemoAccount });
  }, [isPhoneAnalyticsDemoAccount, setPhoneAnalyticsStore]);

  useEffect(() => {
    setMessagingAnalyticsStore({ demoData: messagingAnalyticsDemoData });
  }, [messagingAnalyticsDemoData, setMessagingAnalyticsStore]);

  useEffect(() => {
    setMessagingAnalyticsStore({ isDemoAccount: isMessagingAnalyticsDemoAccount });
  }, [isMessagingAnalyticsDemoAccount, setMessagingAnalyticsStore]);

  const formattedLastMonthDates = {
    startDate: dayjs(filters.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    endDate: dayjs(filters.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
  };

  return (
    <Page>
      <Page.Header>
        <Page.Header.Heading css={styles.heading}>
          <Page.Header.Title title={<Heading level={1}>{t('Analytics')}</Heading>} />
          <Page.Header.Subtitle subtitle={t('Showing results for last 30 days.')} />
          <Page.Header.Action>
            <span css={styles.actions}>
              <GeneralFeedbackButton />
              {hasAnalyticsReportingAccess && <GenerateAnalyticsReportCTA />}
            </span>
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>

      <Page.Body>
        <RoiOverviewCards
          setHasRoiCards={setHasRoiCards}
          style={{ marginBottom: theme.spacing(hasRoiCards ? 5 : 0) }}
        />
        <div css={styles.chartsWrapper}>
          <PhoneIncomingCategoriesOverviewCard filters={filters} />
          <AppointmentNoShowRateChart
            isOnDashboard
            filters={{
              ...formattedLastMonthDates,
              filterType: TimeFilterType.DAILY,
              locationId: preselectedAccessibleLocationIds,
            }}
          />
          {messagingEnabled && <MessagingCategoriesOverviewCard filters={filters} />}
          {isPAEligibleAccount && <PracticeAnalyticsOnDashboard filters={filters} />}
          {hasAutoRecallAnalyticsFeature && <AutoRecallOnDashboard filters={filters} />}
          <AppointmentBreakdownChart
            renderOnlyChart
            filters={{
              ...formattedLastMonthDates,
              filterType: TimeFilterType.DAILY,
              locationId: preselectedAccessibleLocationIds,
            }}
            aggregatesData={undefined}
            aggregatesDataLoading={false}
          />
        </div>
      </Page.Body>
    </Page>
  );
};

const styles = {
  chartsWrapper: css`
    column-gap: ${theme.spacing(3)};
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;

    > section {
      flex-grow: 1;
    }

    @container (min-width: ${breakpoints.medium.max}px) {
      flex-direction: row;

      > section {
        width: calc(50% - ${theme.spacing(1.5)});
      }
    }
  `,

  heading: css`
    @media screen and (max-width: ${breakpoints.xsmall.max}px) {
      margin-bottom: ${theme.spacing(2)};
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
};
